import { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import { endpoints } from "../lib/api";
import { MagnifyingGlass, ArrowClockwise, Brain } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const fmt = (n, d = 2) =>
  n === undefined || n === null
    ? "-"
    : Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

const sideColor = (d) =>
  d === "LONG" ? "text-signal-buy" : d === "SHORT" ? "text-signal-sell" : "text-muted-foreground";

const ageOf = (ts) => {
  if (!ts) return "-";
  const s = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
};

export default function Scanner() {
  const [symbols, setSymbols] = useState([]);
  const [results, setResults] = useState({}); // symbol -> neurobro result
  const [query, setQuery] = useState("");
  const [scanStatus, setScanStatus] = useState({ running: false, done: 0, total: 0 });
  const [rowLoading, setRowLoading] = useState({});
  const [src, setSrc] = useState("neurobro");

  const loadResults = () => {
    endpoints.scannerResults().then((r) => {
      const map = {};
      (r.data.results || []).forEach((it) => (map[it.symbol] = it));
      setResults(map);
    }).catch(() => {});
  };

  const loadStatus = () => {
    endpoints.scannerStatus().then((r) => setScanStatus(r.data)).catch(() => {});
  };

  useEffect(() => {
    endpoints.symbols(100).then((r) => setSymbols(r.data.symbols || []));
    endpoints.settings().then((r) => setSrc(r.data.signal_source || "neurobro")).catch(() => {});
    loadResults();
    loadStatus();
    const id = setInterval(() => {
      loadResults();
      loadStatus();
    }, 8000);
    return () => clearInterval(id);
  }, []);

  const scanAll = async () => {
    try {
      const { data } = await endpoints.scannerScanAll();
      if (data.started) {
        toast.success("Scan AI dimulai — hasil muncul bertahap di tabel.");
      } else {
        toast.info("Scan sedang berjalan...");
      }
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai scan — cek API key AI di Settings.");
    }
  };

  const scanOne = async (sym) => {
    setRowLoading((p) => ({ ...p, [sym]: true }));
    try {
      const { data } = await endpoints.neurobro(sym);
      setResults((prev) => ({ ...prev, [sym]: { ...data, ts: new Date().toISOString() } }));
    } catch (e) {
      toast.error(`Gagal analisa ${sym}`);
    } finally {
      setRowLoading((p) => ({ ...p, [sym]: false }));
    }
  };

  const filtered = useMemo(
    () => symbols.filter((s) => s.symbol.toLowerCase().includes(query.toLowerCase())),
    [symbols, query]
  );

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar />
      <main className="flex-1 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Market Radar
            </div>
            <h1 className="font-heading font-black text-3xl tracking-tight flex items-center gap-2">
              {src === "claude" ? "Claude Scanner" : "Neurobro Scanner"}
              <Brain size={24} weight="fill" className={src === "claude" ? "text-signal-brand" : "text-signal-buy"} />
            </h1>
            <div className="text-[11px] text-muted-foreground mt-1 font-mono">
              Semua sinyal entry bot berasal 100% dari analisa {src === "claude" ? "Claude AI (claude-fable-5)" : "Neurobro AI"}.
            </div>
          </div>
          <Button
            data-testid="scan-all-btn"
            onClick={scanAll}
            disabled={scanStatus.running}
            variant="outline"
            className="rounded-sm"
          >
            <ArrowClockwise size={14} className={`mr-2 ${scanStatus.running ? "animate-spin" : ""}`} />
            {scanStatus.running
              ? `Scanning ${scanStatus.done}/${scanStatus.total}...`
              : `Scan All (${symbols.length})`}
          </Button>
        </div>

        <div className="flex items-center gap-2 bg-[#0d0d0d] border border-border px-3 py-2 rounded-sm max-w-md">
          <MagnifyingGlass size={14} className="text-muted-foreground" />
          <Input
            data-testid="scanner-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter 100 futures pairs..."
            className="bg-transparent border-none outline-none font-mono h-6 px-0 focus-visible:ring-0"
          />
        </div>

        <div className="border border-border rounded-sm bg-[#0d0d0d] overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
                <th className="text-left px-4 py-2">Symbol</th>
                <th className="text-right px-4 py-2">Price</th>
                <th className="text-right px-4 py-2">24h Chg</th>
                <th className="text-right px-4 py-2">Volume</th>
                <th className="text-right px-4 py-2">AI Signal</th>
                <th className="text-right px-4 py-2">Confidence</th>
                <th className="text-right px-4 py-2">Risk</th>
                <th className="text-right px-4 py-2">Umur</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {filtered.map((s) => {
                const r = results[s.symbol];
                const side = r?.error ? "ERR" : r?.side || (r ? "NEUTRAL" : null);
                return (
                  <tr
                    key={s.symbol}
                    className="border-b border-border hover:bg-[#141414]"
                    data-testid={`scan-row-${s.symbol}`}
                  >
                    <td className="px-4 py-3 font-bold">{s.symbol}</td>
                    <td className="text-right px-4 py-3">${fmt(s.last_price, s.last_price < 1 ? 6 : 2)}</td>
                    <td
                      className={`text-right px-4 py-3 ${
                        s.price_change_pct >= 0 ? "text-signal-buy" : "text-signal-sell"
                      }`}
                    >
                      {s.price_change_pct >= 0 ? "+" : ""}
                      {s.price_change_pct.toFixed(2)}%
                    </td>
                    <td className="text-right px-4 py-3 text-muted-foreground">
                      ${fmt(s.quote_volume / 1e6, 1)}M
                    </td>
                    <td className={`text-right px-4 py-3 font-bold ${side === "ERR" ? "text-signal-warn" : sideColor(side)}`}>
                      {side || "-"}
                    </td>
                    <td className={`text-right px-4 py-3 ${sideColor(side)}`}>
                      {r && !r.error ? `${r.confidence ?? 0}%` : "-"}
                    </td>
                    <td className="text-right px-4 py-3 uppercase text-muted-foreground">
                      {r?.risk || "-"}
                    </td>
                    <td className="text-right px-4 py-3 text-muted-foreground">{ageOf(r?.ts)}</td>
                    <td className="text-right px-4 py-3">
                      <Button
                        data-testid={`scan-signal-${s.symbol}`}
                        onClick={() => scanOne(s.symbol)}
                        disabled={!!rowLoading[s.symbol]}
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm"
                      >
                        {rowLoading[s.symbol] ? "..." : "Scan"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
